import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Errors } from '@lub0v/santa-utils';
import type { SessionObject } from '@lub0v/santa-utils';
import { santaClient } from '@src/state/client';

export type SessionStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface SessionState {
  status: SessionStatus;
  user: SessionObject | null;
  error: string | null;
}

const initialState: SessionState = {
  status: 'idle',
  user: null,
  error: null,
};

export const loadSession = createAsyncThunk<SessionObject | null, { background?: boolean } | undefined>('session/load', async () => {
  const result = await santaClient.session();
  if (result.ok) {
    return (result.data as SessionObject | undefined) ?? null;
  }
  // A signed-out visitor is a 404 USER_NOT_LOGGED_IN, not a failure — the
  // backend has no anonymous session to describe. Anything else is real.
  if (result.error?.message === Errors[Errors.USER_NOT_LOGGED_IN]) {
    return null;
  }
  throw new Error(result.error?.message ?? 'request failed');
});

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    userThemeSaved(state, action: { payload: string }) {
      if (state.user) state.user.theme = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSession.pending, (state, action) => {
        if (!action.meta.arg?.background || !state.user) state.status = 'loading';
        state.error = null;
      })
      .addCase(loadSession.fulfilled, (state, action) => {
        state.status = 'ready';
        state.user = action.payload;
      })
      .addCase(loadSession.rejected, (state, action) => {
        if (action.meta.arg?.background && state.user) return;
        state.status = 'failed';
        state.error = action.error.message ?? 'unknown error';
      });
  },
});

export const { userThemeSaved } = sessionSlice.actions;
export default sessionSlice.reducer;
